import {
  type EdgeId,
  type NodeSn,
  getRecordKeys,
  isPathLookupNodeData,
  safeAbsurd,
} from '@ipfabric/utils';
import type {
  PacketDetail,
  PathLookupPathInspectorSelectedPacketSource,
  PathLookupPathInspectorSelectedPackets,
} from 'components/DeviceView/deviceViewTypes';
import type { GraphData } from 'components/Graph/model/GraphData';
import type { Draft } from 'immer';
import { addTrafficScoreToPacketDetail } from './addTrafficScoreToPacketDetail';
import { findNodeIdByNodeSn } from './findNodeIdByNodeSn';
import { getGeneratedPacketDetail } from './getGeneratedPacketDetail';
import { getPacketEdgeDetail } from './getPacketEdgeDetail';
import { getTerminatedPacketDetail } from './getTerminatedPacketDetail';
import { isGeneratedPacketData } from './isGeneratedPacketData';
import { isTerminatedPacketData } from './isTerminatedPacketData';

const updateEdgePacket = (
  pathLookupDetailId: EdgeId,
  source: PathLookupPathInspectorSelectedPacketSource | null,
  graph: GraphData,
  nodeSn: NodeSn,
): PacketDetail | null => {
  if (source === 'edge') {
    return getPacketEdgeDetail(graph, pathLookupDetailId);
  }
  const nodeId = findNodeIdByNodeSn(graph, nodeSn);
  if (nodeId === null) {
    return null;
  }
  const nodeData = graph.nodes[nodeId];
  if (
    nodeData !== undefined &&
    isPathLookupNodeData(nodeData) &&
    source !== null
  ) {
    const nodeInternalPacketData = nodeData[source]?.[pathLookupDetailId];
    if (nodeInternalPacketData === undefined) {
      return null;
    }
    if (isGeneratedPacketData(nodeInternalPacketData)) {
      return getGeneratedPacketDetail(
        pathLookupDetailId,
        nodeInternalPacketData,
      );
    }
    if (isTerminatedPacketData(nodeInternalPacketData)) {
      return getTerminatedPacketDetail(
        pathLookupDetailId,
        nodeInternalPacketData,
      );
    }
    safeAbsurd(nodeInternalPacketData, 'Unknown packet data type');
  }
  return null;
};

export const refreshPathInspectorPacketsDetail = (
  graph: Draft<GraphData>,
  selectedPackets: Draft<PathLookupPathInspectorSelectedPackets>,
): void => {
  const nodeSns = getRecordKeys(selectedPackets);
  for (const nodeSn of nodeSns) {
    const pathInspectorView = selectedPackets[nodeSn];
    if (pathInspectorView?.selectedIncomingPacket !== null) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      pathInspectorView!.selectedIncomingPacket = updateEdgePacket(
        pathInspectorView!.selectedIncomingPacket.id,
        pathInspectorView!.selectedIncomingPacketSource,
        graph,
        nodeSn,
      );
      addTrafficScoreToPacketDetail(
        pathInspectorView!,
        graph,
        pathInspectorView!.selectedIncomingPacket,
      );
    }
    if (pathInspectorView?.selectedOutgoingPacket !== null) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      pathInspectorView!.selectedOutgoingPacket = updateEdgePacket(
        pathInspectorView!.selectedOutgoingPacket.id,
        pathInspectorView!.selectedOutgoingPacketSource,
        graph,
        nodeSn,
      );
    }
  }
};
